import React from 'react';

import {useCurrentUserState} from 'app/current-user/api';
import {createFormErrorsContext, FormErrorsProps} from 'app/general/forms';
import {
  resetableState,
  ResetableStateInjectedProps,
} from 'app/general/modals/resetable-state-hoc';
import {WizardAccordion} from 'app/general/widgets/WizardAccordion';
import {getObjectName} from 'app/utils/fallbacks';
import {gt} from 'app/utils/i18n';

import {AssigneesInOtherTimezonesWarning} from './AssigneesInOtherTimezonesWarning';
import {
  AllAssignmentsType,
  AssigneesByType,
  AssignForm,
  INITIAL_ASSIGNMENTS_STATE,
  useEffectAfterMounted,
} from './AssignForm';
import {
  globalAssigneesToRawAssignees,
  granularAssigneesToRawAssignees,
} from './AssignForm/assignees-to-raw';
import {BaseAssignTaskModalChrome} from './BaseAssignTaskModalChrome';
import {
  createDraftTaskTemplate,
  TaskTemplateDraftType,
} from './create-draft-task-template';
import {DueDateRecurringForm} from './DueDateRecurringForm';
import {
  AssignedDocType,
  FetchDocumentForm,
  PickDocumentForm,
} from './PickDocumentForm';

import type {WriteTaskTemplateSerializedType} from 'sweet-types';

interface Props extends FormErrorsProps, ResetableStateInjectedProps {
  show: boolean;
  onToggle: () => unknown;
  taskData?: Partial<WriteTaskTemplateSerializedType>;
  assignmentsData?: AllAssignmentsType;
  canChangeDoc?: boolean;
  canMultiAssign?: boolean;
  title?: string;
  lockDueDate?: boolean;
  prependToDueDateForm?: React.ReactNode;
  actionButtonText?: [string, string];
  assignAction: (assignment: {
    task: WriteTaskTemplateSerializedType;
    document: AssignedDocType;
    isMultiAssign: boolean;
  }) => Promise<unknown>;
}

export const BaseAssignTaskModal = resetableState(
  createFormErrorsContext(function BaseAssignTaskModal({
    show,
    onToggle,
    resetState,
    setFormErrors,
    taskData = {},
    assignmentsData = INITIAL_ASSIGNMENTS_STATE,
    canChangeDoc = true,
    canMultiAssign = true,
    title,
    lockDueDate = false,
    prependToDueDateForm,
    actionButtonText,
    assignAction,
    captureFormErrorsFromRejection,
  }: Props): JSX.Element {
    const {timezone} = useCurrentUserState().data!;

    const [isMultiAssign, setMultiAssign] = React.useState<boolean>(false);

    const [task, setTask] = React.useState<TaskTemplateDraftType>(() =>
      createDraftTaskTemplate(timezone, taskData)
    );
    const updateTask = (updates: Partial<WriteTaskTemplateSerializedType>) => {
      setTask((task) => ({...task, ...updates}));
    };

    const [isAssigning, setIsAssigning] = React.useState(false);
    const [activeStep, setStepActive] = React.useState('pick-document');

    // step 1 result, the picked doc & assignment type
    const [selectedDoc, setSelectedDoc] =
      React.useState<AssignedDocType | null>(null);

    const selectedDocName = React.useMemo(
      () => (selectedDoc == null ? undefined : getObjectName(selectedDoc)),
      [selectedDoc]
    );

    // step 2 result, assignees and assignment type
    const [assignments, setAssignments] =
      React.useState<AllAssignmentsType>(assignmentsData);

    // update assignments after fetching task instance data
    useEffectAfterMounted(() => {
      setAssignments(assignmentsData);
    }, [assignmentsData]);

    const assignmentName = React.useMemo(
      () => getAssigneesNames(assignments.global),
      [assignments.global]
    );

    function performAssignAction(): Promise<unknown> {
      setFormErrors({}); // remove form errors
      setIsAssigning(true);

      return assignAction({
        task: task as WriteTaskTemplateSerializedType,
        document: selectedDoc!,
        isMultiAssign,
      }).then(
        (task) => {
          onToggle();
          setIsAssigning(false);
          return task;
        },
        (err) => {
          if (err.status == 400) {
            const errorTab = getFieldErrorTabName(err.data);

            if (errorTab != null) {
              setStepActive(errorTab);
            }
          }
          captureFormErrorsFromRejection(err);
          setIsAssigning(false);
          return Promise.reject(err);
        }
      );
    }

    return (
      <BaseAssignTaskModalChrome
        show={show}
        onToggle={onToggle}
        onClosed={resetState}
        name={title}
      >
        <WizardAccordion
          active={activeStep}
          onChange={(step) => {
            if (step === 'pick-document') {
              if (!canChangeDoc) {
                // lock the pick-document pane when canChangeDoc prop is false
                return;
              }
              // remove current document from draft task
              // if user wants to change the target doc
              updateTask({
                object_id: undefined,
                content_type: undefined,
              });
            }
            setStepActive(step);
          }}
          inModal={true}
          steps={[
            {
              id: 'pick-document',
              headerTitle: gt('Document'),
              headerResult: selectedDocName,
              content: (FooterComponent, onFinish) =>
                task.content_type != null &&
                task.object_id != null &&
                selectedDoc == null ? (
                  <FetchDocumentForm
                    onDocumentFetch={onFinish}
                    contentType={task.content_type}
                    objectId={task.object_id}
                  />
                ) : (
                  <PickDocumentForm
                    FooterComponent={FooterComponent}
                    onPickDocument={onFinish}
                  />
                ),
              onFinish: (result: AssignedDocType) => {
                setSelectedDoc(result);
                setTask((task) => ({
                  ...task,
                  content_type: result.content_type,
                  object_id: result.id,
                }));
                setStepActive('assign');
              },
            },
            {
              id: 'assign',
              headerTitle: gt('Assignees'),
              headerResult: assignmentName,
              content: (FooterComponent) =>
                selectedDoc == null ? null : (
                  <AssignForm
                    canMultiAssign={canMultiAssign}
                    initial={assignments}
                    document={selectedDoc}
                    isMultiAssign={isMultiAssign}
                    setMultiAssign={setMultiAssign}
                    permission={task.permission}
                    setPermission={(permission) => updateTask({permission})}
                    FooterComponent={FooterComponent}
                    canGoToPrevious={canChangeDoc}
                  />
                ),
              onFinish: (assignments: AllAssignmentsType) => {
                setAssignments(assignments);
                updateTask(getTaskTemplateAssigneesFromAssignment(assignments));
                setStepActive('recurring');
              },
            },
            {
              id: 'recurring',
              headerTitle: gt('Due date and recurring'),
              content: (FooterComponent) => (
                <DueDateRecurringForm
                  isAssigning={isAssigning}
                  FooterComponent={FooterComponent}
                  prependToDueDateForm={prependToDueDateForm}
                  appendToDueDateForm={
                    <AssigneesInOtherTimezonesWarning
                      assignedUsers={assignments.global.users}
                      due={task.due}
                    />
                  }
                  lockDueDate={lockDueDate}
                  task={task}
                  updateTask={updateTask}
                  actionButtonText={[
                    actionButtonText?.[0] ?? gt('Assign Task'),
                    actionButtonText?.[1] ?? gt('Assigning'),
                  ]}
                />
              ),
              onFinish: performAssignAction,
            },
          ]}
        />
      </BaseAssignTaskModalChrome>
    );
  })
);

function getAssigneesNames(global: AssigneesByType): React.ReactNode {
  /**
   * Merge all assigned people, teams, roles and create a summary of assignment status
   */

  const messageParts: Array<string> = [];

  for (const [type, singularSuffix, pluralSuffix] of [
    ['users', null, gt('teammates')],
    ['roles', gt('role'), gt('roles')],
    ['teams', gt('team'), gt('teams')],
  ] as const) {
    const assignees = global[type];

    if (assignees != null && assignees.length) {
      if (assignees.length === 1) {
        const {name} = assignees[0];
        messageParts.push(
          singularSuffix == null ? name : `${name} ${singularSuffix}`
        );
      } else {
        messageParts.push(`${assignees.length} ${pluralSuffix}`);
      }
    }
  }

  return messageParts.length > 0 ? messageParts.join(', ') : null;
}

/**
 * Given a filed name, return the tab name where the field is located
 * So on 400 errors, the wizard will expand the tab so the user can read the error msg
 */
function getFieldErrorTabName(
  response: null | {[field: string]: string[]}
): undefined | 'pick-document' | 'assign' | 'recurring' {
  const errorFields = Object.keys(response ?? {});

  const tabsFields = [
    {tab: 'pick-document', fields: ['content_type', 'object_id']},
    {tab: 'assign', fields: ['user_ids', 'role_ids', 'team_ids']},
  ];

  for (const {tab, fields} of tabsFields) {
    if (fields.filter((f) => errorFields.indexOf(f) > -1).length > 0) {
      return tab as 'pick-document' | 'assign' | 'recurring';
    }
  }
}

function getTaskTemplateAssigneesFromAssignment(
  assignment: AllAssignmentsType
): Pick<
  WriteTaskTemplateSerializedType,
  'user_ids' | 'role_ids' | 'team_ids' | 'assignment_ids'
> {
  return {
    ...globalAssigneesToRawAssignees(assignment.global),
    assignment_ids: granularAssigneesToRawAssignees(assignment.granular),
  };
}
